import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { inSupportSession } from '../utils/supportSession';
import { useTheme } from '@mui/material/styles';
import { AlertTriangle, CreditCard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import stripeService from '../services/stripeService';
import { buildPaymentRecoveryMessage } from '../utils/billingRecoveryMessage';
import { needsBillingSetup, BILLING_SETUP_ROLES } from '../utils/billingSetup';
import { bannerFromPaidThrough, paidThroughMessage } from '../utils/paidThroughBanner';

const PAYMENT_RECOVERY_STATUSES = new Set(['past_due', 'unpaid', 'incomplete']);
const NO_CHARGE_STATUSES = new Set(['comped', 'free', 'manual']);
const MANUAL_BILLING_MODES = new Set(['manual', 'comped', 'free', 'no_charge']);

function normalizedLower(value) {
  return String(value || '').trim().toLowerCase();
}

function isNoChargeSubscription(subscription = {}) {
  const status = normalizedLower(subscription.status);
  const billingMode = normalizedLower(subscription.billingMode);

  return Boolean(
    subscription.comped === true ||
    NO_CHARGE_STATUSES.has(status) ||
    MANUAL_BILLING_MODES.has(billingMode)
  );
}

function toMillis(value) {
  if (!value) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value < 1e12 ? value * 1000 : value;

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function formatDate(value) {
  const millis = toMillis(value);
  if (!millis) return null;

  return new Date(millis).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function hasPaymentRecoveryIssue(subscription = {}) {
  if (isNoChargeSubscription(subscription)) {
    return false;
  }

  const status = normalizedLower(subscription.status);

  if (status === 'canceled' || status === 'cancelled') {
    return false;
  }

  return Boolean(
    subscription.paymentUpdateRequired
    || PAYMENT_RECOVERY_STATUSES.has(status)
    || (status === 'suspended' && (subscription.paymentFailedAt || subscription.paymentFailureGraceEndsAt))
  );
}

function isPastGracePeriod(subscription = {}) {
  if (isNoChargeSubscription(subscription)) {
    return false;
  }

  const graceEndsAt = toMillis(subscription.paymentFailureGraceEndsAt);
  if (graceEndsAt) {
    return Date.now() >= graceEndsAt;
  }

  return normalizedLower(subscription.status) === 'suspended';
}

function BillingRecoveryBanner() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, organization, userData } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [paidThrough, setPaidThrough] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [error, setError] = useState(null);

  const effectiveSubscription = useMemo(() => (
    subscription || organization?.subscription || null
  ), [subscription, organization?.subscription]);

  // Billing step 2: new-system accounts are decided by their paid-through date alone.
  const paidThroughState = bannerFromPaidThrough(paidThrough);
  const hasIssue = paidThroughState ? paidThroughState.hasIssue : hasPaymentRecoveryIssue(effectiveSubscription || {});
  const pastGrace = paidThroughState ? paidThroughState.pastGrace : isPastGracePeriod(effectiveSubscription || {});
  const isDeveloperBypass = ['developer', 'superuser'].includes(userData?.role);
  const canManageBilling = isDeveloperBypass || BILLING_SETUP_ROLES.includes(userData?.role) || organization?.id === 'org_001';
  const shouldShow = Boolean(currentUser && organization?.id && hasIssue);
  const graceDate = formatDate(paidThroughState ? paidThroughState.deadline : effectiveSubscription?.paymentFailureGraceEndsAt);
  // An account that never finished checkout (no subscription at all): ask its owners/admins to start a subscription.
  // Information only - nothing is blocked.
  const showSetup = Boolean(currentUser && !hasIssue && !inSupportSession()
    && BILLING_SETUP_ROLES.includes(userData?.role)
    && needsBillingSetup(effectiveSubscription, organization)
    && !['/signup', '/admin/billing'].includes(location.pathname));

  const loadSubscriptionStatus = useCallback(async ({ silent = false } = {}) => {
    if (!currentUser || !organization?.id || inSupportSession()) return; // AC1: support logins never see billing

    if (!silent) setLoading(true);
    setError(null);

    try {
      const data = await stripeService.getSubscriptionStatus();
      setSubscription(data.subscription || null);
      setPaidThrough(data.paidThrough || null);
    } catch (err) {
      const status = err.response?.status;
      if (status !== 404) {
        setError('Could not refresh billing status.');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [currentUser, organization?.id]);

  useEffect(() => {
    setSubscription(null);
    setPaidThrough(null);
    loadSubscriptionStatus();
  }, [loadSubscriptionStatus]);

  useEffect(() => {
    if (!shouldShow) return undefined;

    const refreshTimer = setInterval(() => {
      loadSubscriptionStatus({ silent: true });
    }, 60 * 1000);

    const refreshOnFocus = () => loadSubscriptionStatus({ silent: true });
    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnFocus);

    return () => {
      clearInterval(refreshTimer);
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnFocus);
    };
  }, [loadSubscriptionStatus, shouldShow]);

  useEffect(() => {
    if (
      !shouldShow
      || !pastGrace
      || isDeveloperBypass
      || ['/admin/billing', '/support'].includes(location.pathname)
    ) {
      return;
    }

    navigate('/admin/billing', { replace: true, state: { billingRecovery: true } });
  }, [isDeveloperBypass, location.pathname, navigate, pastGrace, shouldShow]);

  const handleUpdatePayment = async () => {
    if (!organization?.id || openingPortal) return;

    setOpeningPortal(true);
    setError(null);

    try {
      const { url } = await stripeService.createPaymentUpdateSession(organization.id);
      window.location.assign(url);
    } catch (err) {
      console.error('[BillingRecoveryBanner] Error opening payment update:', err);
      setError('Could not open the secure payment update page. Please try again or contact support.');
      setOpeningPortal(false);
    }
  };

  if (!shouldShow && showSetup) {
    return (
      <div style={{ backgroundColor: '#fff7ed', color: '#7c2d12', borderBottom: '1px solid #fed7aa', padding: '12px 20px' }}>
        <div style={{
          maxWidth: '1400px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '16px', flexWrap: 'wrap', textAlign: 'left',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
            <AlertTriangle size={20} />
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700 }}>Finish setting up billing</div>
              <div style={{ fontSize: '13px', lineHeight: 1.4, color: '#9a3412' }}>
                This account doesn't have a subscription yet. Start your subscription to keep using Car Wash Minder.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/signup')}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '9px 14px',
              minHeight: '38px', backgroundColor: theme.palette.primary.main, color: '#ffffff', border: 'none',
              borderRadius: '8px', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
            }}
          >
            <CreditCard size={16} />
            Start subscription
          </button>
        </div>
      </div>
    );
  }

  if (!shouldShow) return null;

  const title = pastGrace ? 'Payment update required to restore access' : 'Payment method needs attention';
  const body = paidThroughState
    ? paidThroughMessage(paidThroughState, graceDate)
    : pastGrace
      ? 'Dashboard access is paused until the payment method is updated.'
      : buildPaymentRecoveryMessage(effectiveSubscription || {}, graceDate);

  return (
    <div style={{
      backgroundColor: pastGrace ? '#7f1d1d' : '#fff7ed',
      color: pastGrace ? '#ffffff' : '#7c2d12',
      borderBottom: `1px solid ${pastGrace ? '#991b1b' : '#fed7aa'}`,
      padding: '12px 20px',
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap',
        textAlign: 'left',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          <AlertTriangle size={20} />
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700 }}>
              {title}
            </div>
            <div style={{ fontSize: '13px', lineHeight: 1.4, color: pastGrace ? '#fee2e2' : '#9a3412' }}>
              {loading ? 'Checking billing status...' : body}
              {error ? ` ${error}` : ''}
            </div>
          </div>
        </div>

        {/* Billing step 8: only owners/admins can change the card (the server refuses anyone else). */}
        {!canManageBilling ? (
          <div style={{ fontSize: '13px', fontWeight: 600 }}>Ask an admin of your account to update the card.</div>
        ) : <button
          type="button"
          onClick={handleUpdatePayment}
          disabled={openingPortal}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '9px 14px',
            minHeight: '38px',
            backgroundColor: pastGrace ? '#ffffff' : theme.palette.primary.main,
            color: pastGrace ? '#7f1d1d' : '#ffffff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 700,
            cursor: openingPortal ? 'not-allowed' : 'pointer',
            opacity: openingPortal ? 0.65 : 1,
          }}
        >
          <CreditCard size={16} />
          {openingPortal ? 'Opening...' : 'Update Card'}
        </button>}
      </div>
    </div>
  );
}

export default BillingRecoveryBanner;
